import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { POS_CATEGORY_ICONS, getPosCategoryIcon } from '@/constants/posCategoryIcons';
import { useTranslation } from 'react-i18next';

interface Props {
  value?: string | null;
  onChange: (name: string) => void;
}

export default function IconPicker({ value, onChange }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return POS_CATEGORY_ICONS;
    return POS_CATEGORY_ICONS.filter((entry) => {
      const label = t(entry.labelKey, { defaultValue: entry.name }).toLowerCase();
      return entry.name.toLowerCase().includes(q) || label.includes(q);
    });
  }, [search, t]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search')}
          className="input-field pl-9 w-full text-sm"
        />
      </div>
      <div className="grid grid-cols-8 lg:grid-cols-10 gap-1.5 max-h-56 overflow-y-auto p-1">
        {filtered.map((entry) => {
          const Icon = entry.icon;
          const selected = value === entry.name;
          return (
            <button
              key={entry.name}
              type="button"
              onClick={() => onChange(entry.name)}
              title={t(entry.labelKey, { defaultValue: entry.name })}
              className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border transition-colors ${
                selected
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-5 h-5" />
            </button>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <p className="text-xs text-gray-500 text-center py-2">{t('common.noResults')}</p>
      )}
    </div>
  );
}

export { getPosCategoryIcon };
